import React, { useState, useEffect } from 'react';
import { Card, Upload, Button, Empty, Divider, Space, Modal } from 'antd';
import { UnitsApi } from 'api/api';
import { UnitAttachmentDto } from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { DocumentGrid, DocumentItem, StyledCard } from '../styles';
import { formatImagePath, getFileIcon } from 'utility/utility';
import openNotificationWithIcon from 'utility/notification';
import { ImageGalleryModal } from 'components/ImageGalleryModal';

interface UnitAttachmentsProps {
  unitId: number;
  attachments?: UnitAttachmentDto[];
  loading?: boolean;
  showUpload?: boolean;
  onRefresh: () => void;
}

const unitsApi = new UnitsApi();

export const UnitAttachments: React.FC<UnitAttachmentsProps> = ({ unitId, attachments, loading, showUpload = true, onRefresh }) => {
  const { t } = useTranslation();
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const getImageAttachments = () => {
    return attachments?.filter(att => att.fileType?.startsWith('image/')) || [];
  };

  const getCurrentImage = () => {
    const images = getImageAttachments();
    return images[selectedImageIndex]?.filePath;
  };

  const downloadAttachment = async (attachmentId: string, fileName: string) => {
    try {
      const response = await new UnitsApi().apiUnitsIdAttachmentsAttachmentIdGet(
        {
          id: unitId,
          attachmentId: attachmentId,
        },
        { responseType: 'blob' },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      openNotificationWithIcon('success', t('units:attachment-downloaded', 'Attachment downloaded successfully'));
    } catch (error) {
      openNotificationWithIcon('error', t('units:download-error', 'Failed to download attachment'));
    }
  };

  const deleteAttachment = async (attachmentId: string) => {
    try {
      await new UnitsApi().apiUnitsIdAttachmentsAttachmentIdDelete({
        id: unitId,
        attachmentId: attachmentId,
      });
      onRefresh();
      openNotificationWithIcon('success', t('units:attachment-deleted', 'Attachment deleted'));
    } catch (err) {
      openNotificationWithIcon('error', t('units:attachment-delete-failed', 'Failed to delete attachment'));
    }
  };

  const uploadAttachment = async (
    file: File,
    onSuccess?: (response: any) => void,
    onError?: (error: Error) => void,
  ) => {
    try {
      await unitsApi.apiUnitsIdAttachmentsPost({
        id: unitId,
        unitId: unitId,
        file: file,
      });
      onSuccess?.('ok');
      onRefresh();
      openNotificationWithIcon('success', t('units:attachment-uploaded', 'Attachment uploaded successfully'));
    } catch (err) {
      onError?.(err as Error);
      openNotificationWithIcon('error', t('units:attachment-upload-failed', 'Failed to upload attachment'));
    }
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setSelectedImageIndex(prev =>
          prev > 0 ? prev - 1 : attachments!.filter(att => att.fileType?.startsWith('image/')).length - 1,
        );
      } else if (e.key === 'ArrowRight') {
        setSelectedImageIndex(prev =>
          prev < attachments!.filter(att => att.fileType?.startsWith('image/')).length - 1 ? prev + 1 : 0,
        );
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [attachments]);

  return (
    <StyledCard
      title={t('units:attachments', 'Attachments')}
      loading={loading}
      style={{ flex: 1, border: 'none' }}
      extra={
        <Upload
          customRequest={({ file, onSuccess, onError }) => uploadAttachment(file as File, onSuccess, onError)}
          showUploadList={false}
        >
          {showUpload && (
            <Button type="primary" icon={<FeatherIcon style={{ marginRight: '5px' }} icon="upload" size={16} />}>
              {t('units:upload', 'Upload')}
            </Button>
          )}
        </Upload>
      }
    >
      {attachments && attachments.length > 0 ? (
        <>
          {/* Images Section */}
          {attachments.some(att => att.fileType?.startsWith('image/')) && (
            <div className="image-grid">
              {attachments
                .filter(att => att.fileType?.startsWith('image/'))
                .map((image, index) => (
                  <div key={image.id} className="image-item">
                    <img
                      src={formatImagePath(image.filePath)}
                      alt={image.fileName || ''}
                      onClick={() => {
                        setSelectedImageIndex(index);
                        setIsPreviewVisible(true);
                      }}
                    />
                    <Space className="image-actions">
                      <Button
                        type="text"
                        icon={<FeatherIcon icon="download" size={16} />}
                        onClick={e => {
                          e.stopPropagation();
                          downloadAttachment(image.id!, image.fileName || `attachment-${image.id}`);
                        }}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<FeatherIcon icon="trash-2" size={16} />}
                        onClick={async e => {
                          e.stopPropagation();
                          await deleteAttachment(image.id!);
                        }}
                      />
                    </Space>
                  </div>
                ))}
            </div>
          )}

          {/* Other Documents Section */}
          {attachments.some(att => !att.fileType?.startsWith('image/')) && (
            <>
              <Divider>{t('units:other-documents', 'Other Documents')}</Divider>
              <DocumentGrid>
                {attachments
                  .filter(att => !att.fileType?.startsWith('image/'))
                  .map(doc => {
                    const { icon, color } = getFileIcon(doc.fileName || '');
                    return (
                      <DocumentItem key={doc.id}>
                        <div className="icon-wrapper">
                          <FeatherIcon icon={icon} size={24} style={{ color }} />
                        </div>
                        <div className="filename">{doc.fileName}</div>
                        <Space className="actions" size={2}>
                          <Button
                            type="text"
                            size="small"
                            icon={<FeatherIcon icon="download" size={14} />}
                            onClick={() => downloadAttachment(doc.id!, doc.fileName || `document-${doc.id}`)}
                          />
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<FeatherIcon icon="trash-2" size={14} />}
                            onClick={async () => {
                              await deleteAttachment(doc.id!);
                            }}
                          />
                        </Space>
                      </DocumentItem>
                    );
                  })}
              </DocumentGrid>
            </>
          )}
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('units:no-attachments-added', 'No attachments added yet')}
        />
      )}
      <ImageGalleryModal
        visible={isPreviewVisible}
        onClose={() => setIsPreviewVisible(false)}
        attachments={attachments || []}
      />
    </StyledCard>
  );
};
